import { useQuery } from '@apollo/client/react';

import { LISTING_DOM } from '@modules/listings/listingActivityQueries';

import {
  type PublicationCoverage,
  calculateDaysOnMarket,
  isPublicationHistory,
} from './daysOnMarket';

const LIVE_STATUSES = new Set([
  'available',
  'available_coming_soon',
  'available_off_market',
  'reserved',
  'paused',
]);

const UNPUBLISHED_STATUSES = new Set(['draft', 'pending_review']);

export type DaysOnMarket =
  | { state: 'loading' | 'error' | 'unpublished' | 'unknown' }
  | { state: 'closed'; lotStatus: string; since: Date | null }
  | {
      state: 'live' | 'paused';
      days: number;
      since: Date | null;
      coverage: PublicationCoverage;
      observedSince: Date;
    };

export const useDaysOnMarket = (lotId: string): DaysOnMarket => {
  const { data, loading, error } = useQuery(LISTING_DOM, {
    variables: { lotId },
    pollInterval: 60_000,
    skipPollAttempt: () => document.hidden,
  });

  const publicationHistory = data?.get_lot_publication_history[0];
  if (error != null && publicationHistory == null) {
    return { state: 'error' };
  }
  if (loading && publicationHistory == null) {
    return { state: 'loading' };
  }

  const history: unknown = publicationHistory?.history;
  if (history == null) {
    return { state: 'unknown' };
  }
  if (!isPublicationHistory(history)) {
    return { state: 'error' };
  }
  const result = calculateDaysOnMarket(history);
  const lotStatus = data?.lots_by_pk?.status;
  if (
    lotStatus != null &&
    !LIVE_STATUSES.has(lotStatus) &&
    !UNPUBLISHED_STATUSES.has(lotStatus)
  ) {
    return { state: 'closed', lotStatus, since: result.since };
  }
  if (!result.published) {
    return {
      state:
        result.coverage === 'complete' ||
        (lotStatus != null && UNPUBLISHED_STATUSES.has(lotStatus))
          ? 'unpublished'
          : 'unknown',
    };
  }
  return { ...result, state: result.online ? 'live' : 'paused' };
};
